import React, { useMemo } from 'react';
import { ApiAttachmentForFS } from 'store/models/MainFiles';
import styled from 'styled-components/macro';
import { useDrag } from 'react-dnd';
import { dndConstants } from 'store/constants/dnd.constants';
import SrcThumbnail from './SrcThumbnail';
import { renderFileImage } from '../../main/MainFiles';
import {
  fileSelectedActions,
  previewFileActions,
} from 'store/actions';
import { useDispatch } from 'react-redux';
import { DoubleArrowRounded } from '@material-ui/icons';
import { IconButton, Tooltip } from '@material-ui/core';
import addToSelectedIcon from 'assets/images/add-to-selected-2.svg';
interface SrcFileProps {
  srcFile: ApiAttachmentForFS;
  hasAnyAddableFileSelected: boolean;
  hasAnyTrueAddableFileSelected: boolean;
}
const SrcFile: React.FC<SrcFileProps> = ({
  srcFile,
  hasAnyAddableFileSelected,
  hasAnyTrueAddableFileSelected,
}) => {
  console.debug({ hasAnyTrueAddableFileSelected });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ isDragging }, drag] = useDrag({
    item: { type: dndConstants.SRC_FILE, payload: { srcFile } },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const dispatch = useDispatch();
  const handlePreviewFile = (file: ApiAttachmentForFS) => {
    dispatch(previewFileActions.previewFile(file));
  };

  const handleAddFileToSelectedAtts = (file: ApiAttachmentForFS) => {
    if (hasAnyTrueAddableFileSelected) {
      dispatch(fileSelectedActions.appendSrcFileToAttIndexes(file));
    } else {
      dispatch(fileSelectedActions.appendSrcFile(file, false)); // just put isSelectAll to false for now // TODO: could revisit
    }
  };

  const allUrls = useMemo(
    () =>
      srcFile?.thumbnails && srcFile.thumbnails.map((t) => t.pdfUrl),
    [srcFile],
  );

  const srcFileCanSelect = srcFile.canSelectPage;

  const hideAppendToFileIcon = useMemo(
    () => hasAnyTrueAddableFileSelected && !srcFileCanSelect,
    [hasAnyTrueAddableFileSelected, srcFileCanSelect],
  );
  return (
    <FilenameAndThumbnails
      key={srcFile.partId}
      className={`moveable`}
      ref={drag}
    >
      {/* on click should be able to preview the file */}
      <SFile>
        <SFileIcon>{renderFileImage(srcFile, '25px')}</SFileIcon>
        <SFileNameTitle
          className="link"
          onClick={() => handlePreviewFile(srcFile)}
          title={srcFile.filename}
        >
          {srcFile.filename}
        </SFileNameTitle>
        {!hideAppendToFileIcon && (
          <Tooltip
            title={
              hasAnyTrueAddableFileSelected
                ? 'Append All Pages to The Selected Files'
                : 'Add file'
            }
          >
            <SAddFileButton
              size="small"
              color="primary"
              onClick={(e) => handleAddFileToSelectedAtts(srcFile)}
              disabled={
                hasAnyTrueAddableFileSelected &&
                !srcFile.canSelectPage
              }
            >
              {hasAnyTrueAddableFileSelected ? (
                <SAddToSelectedIcon
                  src={addToSelectedIcon}
                  alt="add-to-selected"
                />
              ) : (
                <DoubleArrowRounded />
              )}
            </SAddFileButton>
          </Tooltip>
        )}
      </SFile>
      {/* only show thumnails if file is processed */}
      {srcFile.canSelectPage && (
        <ThumbnailsWrapper>
          {!srcFile.thumbnails.length ? (
            <SNoThumbnail>No Thumbnails</SNoThumbnail>
          ) : (
            srcFile.thumbnails.map((thumbnail, i) => (
              <SrcThumbnail
                key={thumbnail.num}
                thumbnail={thumbnail}
                srcFile={srcFile}
                hasAnyAddableFileSelected={hasAnyAddableFileSelected}
                index={i}
                allUrls={allUrls}
              />
            ))
          )}
        </ThumbnailsWrapper>
      )}
    </FilenameAndThumbnails>
  );
};

export default SrcFile;
const SFile = styled.div`
  display: flex;
  align-items: center;
`;
const SFileIcon = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
  margin-left: 2px; // to match select all
`;
const SFileNameTitle = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  font-size: ${(props) => props.theme.typography.fontSize.title};
  white-space: nowrap;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const FilenameAndThumbnails = styled.div`
  & + & {
    margin-top: 0.5rem;
  }
`;
const ThumbnailsWrapper = styled.div`
  margin-top: 0.2rem;
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.5rem;
  row-gap: 0.5rem;
`;

const SNoThumbnail = styled.div`
  text-align: center;
  font-size: ${(props) => props.theme.typography.fontSize.body};
  line-height: ${(props) => props.theme.typography.lineHeight.title};
  color: ${(props) => props.theme.palette.text.secondary};
  background: ${(props) => props.theme.palette.grey[50]};
`;
const SAddFileButton = styled(IconButton)`
  &.MuiIconButton-root {
    margin-left: 0.5rem;
  }
`;
const SAddToSelectedIcon = styled.img`
  width: 25px;
`;
